import React, {
  useEffect,
  useCallback,
  createContext,
  useMemo,
  Component,
  forwardRef,
  useImperativeHandle,
  useRef,
} from 'react'
import PropTypes from 'prop-types'
import { connect } from 'react-redux'

import { Animated, StyleSheet, View, Linking } from 'react-native'

import { transitions } from '@adalo/constants'
import { TARGET_RESET_PASSWORD } from 'utils/forgotPassword'
import { createStack, push, peek } from 'utils/stacks'
import { showNotification } from 'ducks/notifications'
import { resetRandomNumbers } from 'ducks/random'

// Import custom hooks
import {
  useNavigationState,
  useTransitionAnimations,
  useNavigationHandlers,
  useNotificationSetup,
  useBackHandler,
  useScreenRendering,
} from '../../hooks'

// Import transition constants from hooks
import {
  SLIDE_TRANSITIONS,
  FLOAT_TRANSITIONS,
} from '../../hooks/useTransitionAnimations'

// Export constants for backwards compatibility
export { SLIDE_TRANSITIONS, FLOAT_TRANSITIONS }

// Create NavigatorContext for child components
const NavigatorContext = createContext()

const NavigatorFunctional = forwardRef((props, ref) => {
  const {
    app,
    layoutGuides,
    previewType,
    initialComponentId,
    skipNotifications,
    deviceId: initialDeviceId,
    authenticated,
    userUsedTemporaryLogin,
    onNavigate,
    showNotification,
    resetRandomNumbers,
    getNotificationsURL,
    isPreviewer,
    getWebNotificationConfig,
  } = props

  // Initialize navigation state using custom hook
  const {
    routeStack,
    deviceId,
    baseOffset,
    navigationInProgress,
    pendingNavigations,
    dispatch,
  } = useNavigationState(initialComponentId, initialDeviceId)

  // Initialize transition animations using custom hook
  const {
    currentViewOffset,
    getTiming,
    getAnimationConfig,
    getAnimationStyles,
  } = useTransitionAnimations()

  // Memoize refs to prevent unnecessary re-renders
  const refs = useMemo(
    () => ({
      linkingEventListener: { current: null },
    }),
    []
  )

  // Destructure refs
  const linkingEventListenerRef = refs.linkingEventListener

  // Helper functions
  const notify = useCallback(
    route => {
      if (!route || !onNavigate || !route.target) {
        return
      }
      onNavigate({ componentId: route.target })
    },
    [onNavigate]
  )

  const getDeviceId = useCallback(() => {
    return deviceId
  }, [deviceId])

  // Create refs for functions that will be used by hooks
  const navigateRef = useRef(null)
  const getRouteRef = useRef(null)

  // Create simple helper functions for the hooks
  const simpleGetRoute = useCallback(
    route => ({
      params: {},
      transition: transitions.PUSH,
      ...route,
    }),
    []
  )

  const simpleHandlePasswordReset = useCallback(() => {
    if (userUsedTemporaryLogin && navigateRef.current) {
      navigateRef.current({
        target: TARGET_RESET_PASSWORD,
        transition: transitions.MODAL,
      })
    }
  }, [userUsedTemporaryLogin])

  const simpleSetupRoutes = useCallback(
    (initialProps = null) => {
      const propsToUse = initialProps || props
      const { initialRoute: initRoute, deviceId: initDeviceId } = propsToUse

      let newRouteStack = createStack({
        target: propsToUse.initialComponentId,
        params: {},
        transition: null,
      })

      if (initRoute && getRouteRef.current) {
        newRouteStack = push(newRouteStack, getRouteRef.current(initRoute))
      }

      notify(peek(newRouteStack))

      dispatch({
        type: 'RESET_STATE',
        payload: {
          routeStack: newRouteStack,
          deviceId: initDeviceId,
        },
      })

      currentViewOffset.setValue(0)
    },
    [props, notify, currentViewOffset, dispatch]
  )

  // Initialize navigation handlers hook
  const { navigate, goBack, setURL, getRoute } = useNavigationHandlers({
    routeStack,
    baseOffset,
    navigationInProgress,
    pendingNavigations,
    dispatch,
    currentViewOffset,
    getTiming,
    notify,
    resetRandomNumbers,
    handlePasswordReset: simpleHandlePasswordReset,
    setupRoutes: simpleSetupRoutes,
    initialComponentId,
  })

  // Initialize notification setup hook
  const { setupNotifications } = useNotificationSetup({
    app,
    authenticated,
    getNotificationsURL,
    isPreviewer,
    showNotification,
    dispatch,
    initialComponentId,
    getRoute: simpleGetRoute,
    navigate,
  })

  // Initialize screen rendering hook
  const { renderCurrentScreen, renderPreviousScreen } = useScreenRendering({
    app,
    layoutGuides,
    previewType,
  })

  // Initialize back handler hook
  useBackHandler(goBack)

  // Update refs with the functions from hooks
  navigateRef.current = navigate
  getRouteRef.current = getRoute

  // Create actual functions that use the navigate function from the hook
  const setupRoutesWithNavigate = useCallback(
    (initialProps = null) => {
      const propsToUse = initialProps || props
      const { initialRoute: initRoute, deviceId: initDeviceId } = propsToUse

      let newRouteStack = createStack({
        target: propsToUse.initialComponentId,
        params: {},
        transition: null,
      })

      if (initRoute) {
        newRouteStack = push(newRouteStack, getRoute(initRoute))
      }

      notify(peek(newRouteStack))

      // Reset all state using reducer
      dispatch({
        type: 'RESET_STATE',
        payload: {
          routeStack: newRouteStack,
          deviceId: initDeviceId,
        },
      })

      currentViewOffset.setValue(0)
    },
    [props, getRoute, notify, currentViewOffset, dispatch]
  )

  // Expose methods to parent via ref
  useImperativeHandle(
    ref,
    () => ({
      navigate: (...args) => navigate(...args),
      getDeviceId: () => getDeviceId(),
    }),
    [navigate, getDeviceId]
  )

  // Context value for child components
  const navigatorContextValue = useMemo(
    () => ({
      navigate: (...args) => navigate(...args),
      getDeviceId: () => getDeviceId(),
    }),
    [navigate, getDeviceId]
  )

  // Initialize routes on mount
  useEffect(() => {
    setupRoutesWithNavigate()
  }, [setupRoutesWithNavigate])

  // componentDidMount equivalent
  useEffect(() => {
    if (!routeStack) {
      return
    }

    const currentTarget = peek(routeStack)?.target
    resetRandomNumbers(currentTarget)

    Linking.getInitialURL().then(url => {
      setURL(url)
    })
    linkingEventListenerRef.current = Linking.addEventListener('url', event => {
      setURL(event)
    })

    // Setup notifications
    setupNotifications(skipNotifications, deviceId, getWebNotificationConfig)

    // Cleanup function (componentWillUnmount equivalent)
    return () => {
      if (linkingEventListenerRef.current) {
        linkingEventListenerRef.current.remove()
      }
    }
  }, [
    routeStack,
    resetRandomNumbers,
    setURL,
    skipNotifications,
    setupNotifications,
    deviceId,
    getWebNotificationConfig,
  ])

  // componentDidUpdate equivalent
  useEffect(() => {
    if (!routeStack) {
      return
    }

    const currentTarget = peek(routeStack)?.target

    if (navigationInProgress) {
      resetRandomNumbers(currentTarget)
    }
  }, [routeStack, navigationInProgress, resetRandomNumbers])

  // Process pending navigations when route stack becomes available
  useEffect(() => {
    if (routeStack && pendingNavigations.length > 0) {
      const navigationsToProcess = [...pendingNavigations]
      dispatch({ type: 'CLEAR_PENDING_NAVIGATIONS' })

      // Process each navigation action using microtasks
      navigationsToProcess.reduce(
        (promise, action) => promise.then(() => navigate(action)),
        Promise.resolve()
      )
    }
  }, [routeStack, pendingNavigations, navigate, dispatch])

  // Get current route info
  const currentRoute = routeStack ? peek(routeStack) : null
  const previousRoute = routeStack ? peek(routeStack, 1) : null
  const transition = currentRoute?.transition || transitions.NONE

  // Get animation configuration and styles from the hook
  const animationConfig = getAnimationConfig(transition)
  const animationStyles = useMemo(() => {
    if (!currentRoute?.transition) return null
    return getAnimationStyles(
      currentRoute.transition,
      baseOffset,
      animationConfig
    )
  }, [
    currentRoute?.transition,
    baseOffset,
    animationConfig,
    getAnimationStyles,
  ])

  if (!routeStack) {
    return null
  }

  const currentRouteIndex = routeStack.length - 1
  const previousRouteIndex = routeStack.length - 2

  if (!currentRoute || !currentRoute.target) {
    Promise.resolve().then(() => {
      setupRoutesWithNavigate()
    })
    return null
  }

  // Destructure animation styles after all checks
  const { previousScreenAnimationStyles, currentScreenAnimationStyles } =
    animationStyles || {
      previousScreenAnimationStyles: {},
      currentScreenAnimationStyles: {},
    }

  return (
    <NavigatorContext.Provider value={navigatorContextValue}>
      <View style={styles.wrapper}>
        <Animated.View
          style={previousScreenAnimationStyles}
          key={`${previousRouteIndex}-${previousRoute?.target || '0'}`}
        >
          {renderPreviousScreen(routeStack, previousRoute)}
          {animationConfig.dimUnderView && (
            <Animated.View
              key="fader"
              style={[
                styles.inner,
                styles.fader,
                {
                  opacity: currentViewOffset.interpolate({
                    inputRange: [baseOffset, baseOffset + 1],
                    outputRange: [0, 1],
                  }),
                },
              ]}
            />
          )}
        </Animated.View>
        <Animated.View
          key={`${currentRouteIndex}-${currentRoute.target || '1'}`}
          style={currentScreenAnimationStyles}
        >
          {renderCurrentScreen(routeStack)}
        </Animated.View>
      </View>
    </NavigatorContext.Provider>
  )
})

// PropTypes for functional component
NavigatorFunctional.propTypes = {
  app: PropTypes.shape({}).isRequired,
  layoutGuides: PropTypes.shape({}),
  previewType: PropTypes.string,
  initialComponentId: PropTypes.string.isRequired,
  skipNotifications: PropTypes.bool,
  deviceId: PropTypes.string,
  authenticated: PropTypes.bool,
  userUsedTemporaryLogin: PropTypes.bool,
  onNavigate: PropTypes.func,
  showNotification: PropTypes.func.isRequired,
  resetRandomNumbers: PropTypes.func.isRequired,
  // Context values that are now props
  getNotificationsURL: PropTypes.func,
  isPreviewer: PropTypes.bool,
  getWebNotificationConfig: PropTypes.func,
}

NavigatorFunctional.defaultProps = {
  layoutGuides: {},
  previewType: undefined,
  skipNotifications: false,
  deviceId: undefined,
  authenticated: false,
  userUsedTemporaryLogin: false,
  onNavigate: undefined,
  getNotificationsURL: undefined,
  isPreviewer: false,
  getWebNotificationConfig: undefined,
}

// Wrapper class component to handle legacy context
class Navigator extends Component {
  static contextTypes = {
    getNotificationsURL: PropTypes.func,
    isPreviewer: PropTypes.bool,
    getWebNotificationConfig: PropTypes.func,
  }

  static childContextTypes = {
    navigate: PropTypes.func,
    getDeviceId: PropTypes.func,
  }

  getChildContext() {
    return {
      navigate: (...args) => {
        if (!this.navigatorRef?.navigate) {
          return
        }
        return this.navigatorRef.navigate(...args)
      },
      getDeviceId: () => {
        if (!this.navigatorRef?.getDeviceId) {
          return
        }
        return this.navigatorRef.getDeviceId()
      },
    }
  }

  setNavigatorRef = ref => {
    this.navigatorRef = ref
  }

  render() {
    return (
      <NavigatorFunctional
        {...this.props}
        {...this.context}
        ref={this.setNavigatorRef}
      />
    )
  }
}

const styles = StyleSheet.create({
  wrapper: {
    flex: 1,
    backgroundColor: '#222',
  },
  inner: {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
  },
  fader: {
    backgroundColor: 'rgba(0, 0, 0, 0.3)',
  },
})

const mapStateToProps = state => ({
  userUsedTemporaryLogin: state.auth.userUsedTemporaryLogin,
})

export default connect(mapStateToProps, {
  showNotification,
  resetRandomNumbers,
})(Navigator)
